import { inject, injectable } from 'tsyringe';
import { connectionsClient } from '@/shared/ConnectionsApiClient';
import { platformIdForProviderType, platformLabel } from '@/shared/ProviderPlatform';
import { DirectoryApiService } from '@/features/directory/DirectoryApiService';

/** A selectable filter value: the stored `value` plus a human-readable `label`. */
export interface FilterValueOption {
    value: string;
    label: string;
    /** Set on provider-connection options so the UI can render a platform icon. */
    providerTypeId?: string;
}

const USER_SEARCH_PAGE_SIZE = 20;

/**
 * App-wide value source for the global filter bar. Provider options come from
 * the connections catalog; user options come from a debounced directory search.
 * Independent of any page's analytics gateway so the bar works everywhere.
 *
 * The user `value` is the directory `email` — the verified join key
 * (`interactions.user_id == directory.email`). This resolves users for
 * email-keyed providers (Google Vault/Gemini, Microsoft Copilot); providers
 * that emit opaque `user_id`s (Anthropic, OpenAI, GitHub) won't match by email.
 */
@injectable()
export class GlobalFilterValueService {
    private providerCache: FilterValueOption[] | null = null;
    private readonly providerLabels = new Map<string, string>();
    private readonly providerTypes = new Map<string, string>();

    constructor(@inject(DirectoryApiService) private readonly directory: DirectoryApiService) {}

    async loadProviders(): Promise<FilterValueOption[]> {
        if (this.providerCache !== null) return this.providerCache;
        const connections = await connectionsClient.providerConnectionsApi.getProviderConnections();
        const bases = connections.map((connection) => ({ id: connection.id, base: providerBaseLabel(connection), providerTypeId: connection.providerTypeId }));
        const baseCounts = new Map<string, number>();
        for (const entry of bases) baseCounts.set(entry.base, (baseCounts.get(entry.base) ?? 0) + 1);
        const options = bases.map(({ id, base, providerTypeId }) => ({
            value: id,
            label: (baseCounts.get(base) ?? 0) > 1 ? `${base} · ${id.slice(0, 4)}` : base,
            providerTypeId,
        }));
        for (const option of options) {
            this.providerLabels.set(option.value, option.label);
            if (option.providerTypeId !== undefined) this.providerTypes.set(option.value, option.providerTypeId);
        }
        this.providerCache = options;
        return options;
    }

    async searchUsers(query: string): Promise<FilterValueOption[]> {
        const page = await this.directory.listUsers({ page: 1, pageSize: USER_SEARCH_PAGE_SIZE, search: query });
        return page.items.map((user) => ({
            value: user.email,
            label: user.displayName.length > 0 ? `${user.displayName} · ${user.email}` : user.email,
        }));
    }

    providerLabel(connectionId: string): string {
        return this.providerLabels.get(connectionId) ?? connectionId;
    }

    /** The connection's provider type (for icon lookup), once `loadProviders` has run. */
    providerTypeId(connectionId: string): string | null {
        return this.providerTypes.get(connectionId) ?? null;
    }
}

/** A human label for a connection: its display name, else the platform name, else a prettified provider type — never the raw id. */
function providerBaseLabel(connection: { id: string; displayName?: string | null; providerTypeId: string }): string {
    if (typeof connection.displayName === 'string' && connection.displayName.length > 0) return connection.displayName;
    const platformId = platformIdForProviderType(connection.providerTypeId);
    if (platformId !== null) return platformLabel(platformId);
    return prettifyProviderType(connection.providerTypeId);
}

function prettifyProviderType(providerTypeId: string): string {
    const vendor = providerTypeId.split('@')[0]?.split('/')[0] ?? providerTypeId;
    return vendor
        .split('-')
        .map((word) => (word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
        .join(' ');
}
